/**
 * New Player Welcome Panel
 * Eye-catching panel that directs new players to the quest board
 */

import { useGameStore } from '@/state';

interface NewPlayerWelcomePanelProps {
  onNavigateToMarket?: (marketId: string) => void;
}

export function NewPlayerWelcomePanel({ onNavigateToMarket }: NewPlayerWelcomePanelProps) {
  const activeQuests = useGameStore((state) => state.playerState.quests.activeQuests);
  const completedQuests = useGameStore((state) => state.playerState.quests.completedQuests);
  const totalFishCaught = useGameStore((state) => state.playerState.stats.totalFishCaught);

  // Only show if player is truly new (no active quests, no completed quests, less than 5 fish caught)
  const isNewPlayer = activeQuests.length === 0 && completedQuests.length === 0 && totalFishCaught < 5;

  if (!isNewPlayer) {
    return null;
  }

  const handleGoToQuestBoard = () => {
    if (onNavigateToMarket) {
      // Navigate to camp_chef market (the first market)
      onNavigateToMarket('camp_chef');
    }
  };

  return (
    <div className="bg-gradient-to-br from-yellow-900/40 via-orange-900/40 to-red-900/40 rounded-xl border-2 border-yellow-500 p-6 space-y-4 shadow-lg animate-pulse">
      {/* Header with big icon */}
      <div className="flex items-center justify-center gap-3">
        <span className="text-6xl">👋</span>
      </div>

      {/* Welcome message */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-yellow-100">Welcome to Mountain Lake!</h2>
        <p className="text-yellow-200 text-lg leading-relaxed">
          You've arrived at your first fishing spot. The local{' '}
          <span className="font-bold text-white">Camp Chef</span> has some quests to help you
          learn the ropes and get started.
        </p>
      </div>

      {/* Features showcase */}
      <div className="bg-gray-900/50 rounded-lg p-4 space-y-2">
        <p className="text-sm text-gray-300 text-center font-semibold">What you'll learn:</p>
        <div className="grid grid-cols-1 gap-2 text-sm text-gray-300">
          <div className="flex items-center gap-2">
            <span>🎣</span>
            <span>How to cast your line and catch fish</span>
          </div>
          <div className="flex items-center gap-2">
            <span>💰</span>
            <span>Where to sell your catch for gold</span>
          </div>
          <div className="flex items-center gap-2">
            <span>🧑‍🤝‍🧑</span>
            <span>How to hire buddies to automate fishing</span>
          </div>
          <div className="flex items-center gap-2">
            <span>📦</span>
            <span>Managing your inventory and upgrades</span>
          </div>
        </div>
      </div>

      {/* Big call to action button */}
      {onNavigateToMarket && (
        <button
          onClick={handleGoToQuestBoard}
          className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl border-2 border-yellow-400"
        >
          <div className="flex items-center justify-center gap-3">
            <span className="text-2xl">📋</span>
            <span>Check Out the Quest Board!</span>
            <span className="text-2xl">✨</span>
          </div>
        </button>
      )}

      {/* Subtle hint */}
      <p className="text-xs text-center text-yellow-300/70">
        Tip: You can also access the quest board from the Markets tab at the bottom
      </p>
    </div>
  );
}
